import React from 'react';
import { Form } from 'react-final-form';

import { getEndpoint } from 'api';
import ActionButton from 'components/ActionButton';
import Button from 'components/Button';
import { required, TextInput } from 'components/FinalForm';
import GdprAccordion from 'components/GdprAccordion';
import InfoBox from 'components/InfoBox';
import type { VariantId } from 'models/product';
import { fetchResult } from 'utils/fetch';
import { getFieldErrors } from 'utils/formHelpers';
import { pushToGTM } from 'utils/GoogleTagManager';
import { sendGlobalEvent } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

interface Props {
	onClose: () => void;
	shouldAskForEmail?: boolean;
	userEmail?: string;
	variantId: VariantId;
}

function WatchableSignup({
	onClose,
	shouldAskForEmail,
	userEmail = '',
	variantId,
}: Props) {
	const { t } = useI18n();
	// TODO: do something about the hidden input when hook form is merged
	const onWatchableSignup = async ({ email }: { email: string }) => {
		const response = await fetchResult(
			getEndpoint('WatchProductStock', { queryParams: { email, variantId } }),
		);

		if (response.isSuccess()) {
			pushToGTM({
				type: 'notify_in_stock',
				payload: {
					productId: variantId,
				},
			});
			sendGlobalEvent('engagement', {
				type: 'registeredForBackInStock',
			});
			return undefined;
		}

		return getFieldErrors(response.error);
	};

	return (
		<Form
			onSubmit={onWatchableSignup}
			initialValues={{
				email: userEmail,
			}}
			render={(renderProps) => {
				const { handleSubmit, hasSubmitErrors, submitSucceeded, submitting } =
					renderProps;
				return (
					<>
						{hasSubmitErrors && (
							<InfoBox
								icon="error"
								message={t('general_critical_error_text')}
								variant="error"
								type="default"
								className="mb-4"
							/>
						)}

						{submitSucceeded && (
							<>
								<InfoBox
									icon="info"
									message={t('product_watchable_popover_success_text')}
									variant="success"
									type="default"
									className="mb-4"
								/>
								<Button
									variant="secondary"
									displayWidth="full"
									size="large"
									onClick={onClose}
								>
									{t('popover_close_label')}
								</Button>
							</>
						)}
						<form
							className={submitSucceeded ? 'hidden' : undefined}
							onSubmit={handleSubmit}
						>
							{shouldAskForEmail && (
								<>
									<p>{t('product_watchable_popover_text')}</p>
									<GdprAccordion />
									<TextInput
										type="email"
										id="email"
										className="my-4"
										label={t('account_details_email_label')}
										inputMode="email"
										name="email"
										validate={required()}
									/>
								</>
							)}
							{!shouldAskForEmail && (
								<>
									<p>{t('product_watchable_popover_known_customer_text')}</p>
									<p className="my-4">{`${t('account_details_email_label')}: ${userEmail}`}</p>
									<TextInput
										// @ts-expect-error hidden is not a valid prop
										type="hidden"
										id="email"
										className="hidden"
										label={t('account_details_email_label')}
										inputMode="email"
										name="email"
									/>
								</>
							)}
							<ActionButton
								variant="cta"
								displayWidth="full"
								size="xl"
								customState={
									submitting ? 'loading' : hasSubmitErrors ? 'failure' : 'idle'
								}
								minimunLoadingTime={0}
								type="submit"
							>
								{t('product_watchable_popover_button')}
							</ActionButton>
						</form>
					</>
				);
			}}
		/>
	);
}

WatchableSignup.displayName = 'WatchableSignup';

export default WatchableSignup;
